import React, { useState } from 'react';
import NavBar from '../Welcome/NavBar';
import Footer from '../Welcome/Footer';
import profileImage from '../../assets/img/profile/default.png';
import { BsPersonFillAdd } from 'react-icons/bs';
import DashboardMain from './DashboardMain';
import Notifications from './Notifications';
import { auth } from '../../firebase.config';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import male from "../FamilyNode/male-avatar.png";
import css from "../FamilyNode/FamilyNode.module.css";
import uploadImage from '../../helper/uploadImage';
import API from '../../api/api';
import { Error, Success } from '../Form';
import styles from './Dashboard.module.css';
import { useNotification } from '../../context/NotificationsContext';
import SubscriptionPage from './SubscriptionPage';
import UsersList from './UsersList';
import ServiceInterestList from './ServiceInterestList';

// Define prop types (e.g., for dynamic content)
interface LandingPageProps {
  page: string;
  keywords: string;
  description: string;
  twitter: string;
  services: {
    serviceId: string;
    serviceName: string;
    serviceImage: string;
    serviceDescription: string;
    serviceCode: string;
  }[];
  notifications: {
    notification_id: string;
    notification_date: string;
    user_id: string;
    notification_main: string;
    notification_type: string;
    notification_read: string;
    notification_code: string;
  }[];
}

interface Messages {
  message?: string
}

const Dashboard: React.FC<LandingPageProps> = ({ page, keywords, description, twitter }) => {
  const { user, setUser } = useUser();
  const fullname = user?.firstName + " " + user?.lastName;
  const email = user?.email;
  const phone = user?.phoneNumber;
  const isPremium = user?.isPremium;


  // State to manage the active tab
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isImageOpen, setImageOpen] = useState(false);
  const [uploadedImageFile, setUploadedImageFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [errors, setError] = useState<Messages>({});
  const [success, setSuccess] = useState<Messages>({});
  const [membersCount, setMembersCount] = useState<number>(0);
  const { hasNotification, setHasNotification } = useNotification();
  const toggleImage = () => {
    setImageOpen(!isImageOpen);
  }

  const navigate = useNavigate();
  const handleSignOut = async (e: any) => {
    e.preventDefault();
    try {
      auth.signOut();
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      setUser(null);
      navigate("/login");
    } catch (error) {
      console.error("Sign out error:", error);
    }
  };

  const handleProfileChange = (e: any) => {
    e.preventDefault();
    toggleImage();
   
  }

  const handleSubmitImage = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
  const user = auth.currentUser;

    setIsLoading(true);
    if (!uploadedImageFile) {
      console.error("No image file uploaded");
      return;
    }
    const genelatedImage = await uploadImage(uploadedImageFile, "simple");
    try {
      setError({});
      setSuccess({});

      const token = await user?.getIdToken();
      const response = await API.put(
        '/auth/profleimage',
        { imageUrl: genelatedImage },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 200) {
        setSuccess({
          message: "Profile picture updated successfully.",
        });
      } else {
        setError({
          message: "Failed to update profile picture.",
        });
      }
      setIsLoading(true);
      localStorage.setItem("user", JSON.stringify({ ...user, imageUrl: genelatedImage }));
      toggleImage();
      window.location.reload();

    } catch (error: any) {
      setError({
        message: error.response?.data?.message || 'Please try again',
      });
      console.error("Error updating profile picture:", error);
    } finally {
      setIsLoading(false);
    }


  };
  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedImageFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setUploadedImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleMembersCountUpdate = (newCount: number) => {// Debug log
    setMembersCount(newCount);
  };

  return (
    <div>
      <NavBar />

      <div className="container-xxl bg-white p-0">
        {/* Spinner */}
        <div id="spinner" className="spinner-container">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>

        {/* About Section */}
        <div className="container-xxl py-5">
          <div className="container">
            <div className="row g-4">

              <div className="col-md-3 col-sm-12">
                {/* Profile Card */}
                <div className="team-item rounded overflow-hidden">
                  <div className="position-relative">
                    <button type="button" className="border-0 bg-transparent p-0">
                      <img src={user?.imageUrl || profileImage} className="img-fluid" alt="Profile" />
                    </button>

                    <div className="position-absolute start-50 top-100 translate-middle d-flex align-items-center">
                      <a className="btn btn-square mx-1" href="/" onClick={handleProfileChange}>
                        <BsPersonFillAdd className="text-primary" /> {/* Use the imported icon */}
                      </a>
                    </div>
                  </div>
                  <div className="text-center p-4 mt-3">
                    <h5 className="fw-bold mb-0">{fullname}</h5>
                    <small>{email}</small><br />
                    <small>{phone}</small>
                    <div className='mt-2'>

                      {!isPremium &&  user?.role !== 'admin' && <span className="badge bg-warning text-dark p-2" style={{ fontSize: "0.9rem" }}>
                        <i className="fas fa-crown me-1"></i>
                        Free Plan
                      </span>}

                      {(isPremium || user?.role === 'admin') && <span className="badge bg-success p-2" style={{ fontSize: "0.9rem" }}>
                        <i className="fas fa-crown me-1"></i>
                        Premium Plan
                      </span>}
                    </div>
                  </div>
                </div>

                {/* Friends, Memories, Events */}
                <div className="row mt-4">
                  <div className="col-md-12 col-sm-12">
                    <ul className="list-group list-group-flush">
                      <li className="list-group-item">
                        <strong><i className="fa-sharp fa-light fa-users me-2"></i>Members</strong>
                        <span className="badge badge-sm text-bg-secondary float-end">{membersCount}</span>
                      </li>
                      <li className="list-group-item">

                      </li>

                      {/* <li className="list-group-item">
                        <strong><i className="fa-sharp fa-light fa-calendar-star"></i> Events</strong>
                        <span className="badge badge-sm text-bg-secondary float-end">2</span>
                      </li>  */}


                      <li className="list-group-item">
                        <a href="/family-match">
                          <strong><i className="fa-solid fa-user-magnifying-glass me-1"></i> Fill in to improve matches</strong>
                          {/* <span className="badge badge-sm text-bg-secondary float-end">2</span> */}
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Logout Button */}
                <div className="row mt-4">
                  <div className="col-md-12 col-sm-12">
                    <div className="d-grid gap-2">
                      <button className="btn btn-danger btn-flat" type="button" onClick={handleSignOut}>
                        <i className="fa-sharp fa-light fa-arrow-right-from-bracket mr-2"></i> Logout
                      </button>
                    </div>
                  </div>
                </div>


                <div className="row mt-3">
                  {/* <div className="col-md-12 col-sm-12">
    <div className="d-grid gap-2">
      <button className="btn btn-outline-danger btn-flat" type="button" >
        <i className="fa-sharp fa-light fa-user-xmark mr-2"></i> Delete Account
      </button>
    </div>
  </div> */}
                </div>

              </div>

              {/* Main Dashboard Content */}
              <div className="col-md-9 col-sm-12">
                <div className="team-item p-3">
                  <div className="row">
                    <div className="col-md-12 col-sm-12">
                      <ul className="nav nav-underline nav-fill g-font-size-18--xs" id="myTab" role="tablist">
                        <li className="nav-item" role="presentation">
                          <button
                            className={`nav-link ${activeTab === 'dashboard' ? 'active' : ''}`}
                            onClick={() => setActiveTab('dashboard')}
                            type="button"
                            role="tab"
                            aria-controls="dashboard"
                            aria-selected={activeTab === 'dashboard'}
                          >
                            <i className="fa-sharp fa-light fa-gauge me-2"></i>
                            Dashboard
                          </button>
                        </li>
                        {/* Add Admin Tab */}
                        {/* {user?.role === 'admin' && (
                          <li className="nav-item" role="presentation">
                            <button
                              className={`nav-link ${activeTab === 'admin' ? 'active' : ''}`}
                              onClick={() => setActiveTab('admin')}
                              type="button"
                              role="tab"
                              aria-controls="admin"
                              aria-selected={activeTab === 'admin'}
                            >
                              <i className="fas fa-users-cog me-2"></i>
                              Admin
                            </button>
                          </li>
                        )} */}
                        <li className="nav-item" role="presentation">
                          <button
                            className={`nav-link ${activeTab === 'notifications' ? 'active' : ''}`}
                            onClick={() => {
                              setActiveTab('notifications');
                              setHasNotification(false);
                            }}
                            type="button"
                            role="tab"
                            aria-controls="notifications"
                            aria-selected={activeTab === 'notifications'}
                          >
                            <div className={styles.bellWrapper}>
                              <i className="fa-sharp fa-light fa-bell me-2"></i>
                              {hasNotification && <span className={styles.redDot}></span>}
                            </div>
                          </button>
                        </li>
                        {user?.role === "admin" && 
                        <li className="nav-item" role="presentation">
                          <button
                            className={`nav-link ${activeTab === 'users' ? 'active' : ''}`}
                            onClick={() => setActiveTab('users')}
                            type="button"
                            role="tab"
                            aria-controls="users"
                            aria-selected={activeTab === 'users'}
                          >
                            <i className="fa-sharp fa-light fa-users"></i>
                            Users
                          </button>
                        </li>
}
                        {user?.role === "admin" &&
                        <li className="nav-item" role="presentation">
                          <button
                            className={`nav-link ${activeTab === 'interest' ? 'active' : ''}`}
                            onClick={() => setActiveTab('interest')}
                            type="button"
                            role="tab"
                            aria-controls="interest"
                            aria-selected={activeTab === 'interest'}
                          >
                            <i className="fa-sharp fa-light fa-clipboard-list me-2"></i>
                            Interest Signups
                          </button>
                        </li>
}
                        {/* <li className="nav-item" role="presentation">
                          <button
                            className={`nav-link ${activeTab === 'messages' ? 'active' : ''}`}
                            onClick={() => setActiveTab('messages')}
                            type="button"
                            role="tab"
                            aria-controls="messages"
                            aria-selected={activeTab === 'messages'}
                          >
                            <i className="fa-sharp fa-light fa-messages"></i> Messages
                          </button>
                        </li> */}
                        {/* <li className="nav-item" role="presentation">
                          <button
                            className={`nav-link ${activeTab === 'settings' ? 'active' : ''}`}
                            onClick={() => setActiveTab('settings')}
                            type="button"
                            role="tab"
                            aria-controls="settings"
                            aria-selected={activeTab === 'settings'}
                          >
                            <i className="fa-sharp fa-light fa-gears"></i> Profile
                          </button>
                        </li> */}
                        <li className="nav-item" role="presentation">
                          <button
                            className={`nav-link ${activeTab === 'payments' ? 'active' : ''}`}
                            onClick={() => {
                              console.log("Switching to payments tab"); // Debug log
                              setActiveTab('payments');
                            }}
                            type="button"
                            role="tab"
                            aria-controls="payments"
                            aria-selected={activeTab === 'payments'}
                          >
                            <i className="fa fa-sharp fa-light fa-credit-card me-2"></i>

                            payments
                            {/* <span className="badge badge-sm text-bg-secondary">Paid</span> */}
                          </button>
                        </li>

                      </ul>

                      <div className="tab-content mt-4" id="myTabContent">
                        {activeTab === 'dashboard' && (
                          <div className="tab-pane fade show active" id="dashboard" role="tabpanel">
                            <DashboardMain onMembersCountUpdate={handleMembersCountUpdate} />
                          </div>
                        )}
                        
                        {/* Admin Section */}
                        {/* {user?.role === 'admin' && activeTab === 'admin' && (
                          <div className="tab-pane fade show active" id="admin" role="tabpanel">
                            <CouponManagement />
                          </div>
                        )} */}

                        {activeTab === 'notifications' && (
                          <div className="tab-pane fade show active" id="notifications" role="tabpanel">
                            <Notifications />
                          </div>
                        )}

                        {activeTab === 'users' && (
                          <div className="tab-pane fade show active" id="users" role="tabpanel">
                            <UsersList />
                          </div>
                        )}

                        {activeTab === 'interest' && (
                          <div className="tab-pane fade show active" id="interest" role="tabpanel">
                            <ServiceInterestList />
                          </div>
                        )}

                        {activeTab === 'payments' && (
                          <div className="tab-pane fade show active" id="payments" role="tabpanel">
                            <SubscriptionPage />
                          </div>
                        )}
                        {activeTab === 'settings' && (
                          <div className="tab-pane fade" id="settings" role="tabpanel" aria-labelledby="settings-tab">
                            {/* Replace with your settings/profile component */}
                            <h5>Profile Content Here</h5>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      <Footer />
      {isImageOpen && (
        <div className={css.overlay}>
          <div className={css.formContainer}>
            <h2>Upload Profile Picture</h2>
            <form onSubmit={handleSubmitImage}>
              <div className={css.profileImageContainer}>
                <img
                  id="profileImage"
                  src={uploadedImage || male}
                  alt="Profile Preview"
                  className={css.profilePic}
                />
              </div>
              <div className={css.formGroup}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </div>
              <div className={css.formActions}>
                <button type="submit">{isLoading ? "Loading ..." : "Submit"}</button>
                <button type="button" onClick={toggleImage}>
                  Close
                </button>
              </div>
            </form>
            {errors?.message && <Error>{errors.message}</Error>}
            {success?.message && <Success>{success.message}</Success>}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
